import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Download, DownloadCloud, ChevronLeft, Loader2 } from "lucide-react";
import { Toaster, toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STYLE_TITLES = {
  casino_royale: "Klasični smoking",
  action_scene: "Akcijska scena",
  gala_dinner: "Gala večerja",
  female_agent: "Skrivnostna agentka",
  bg_casino: "Casino Monte Carlo",
  bg_aston: "Nočni London",
  bg_rooftop: "Strešna terasa",
  bg_yacht: "Jahta na Rivieri",
};

// Trigger a browser download from a blob without leaving the page.
function saveBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoke a tick later so the download has a chance to start.
  setTimeout(() => window.URL.revokeObjectURL(url), 2000);
}

export default function Downloads() {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingAll, setDownloadingAll] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/photobooth/gallery`);
      setPhotos(res.data.photos || []);
    } catch (err) {
      console.error("Downloads refresh failed:", err);
      toast.error("Nalaganje galerije ni uspelo.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleDownloadOne = async (photo) => {
    if (downloadingId) return;
    setDownloadingId(photo.id);
    try {
      const res = await axios.get(`${API}/photobooth/photo/${photo.id}?download=1`, {
        responseType: "blob",
      });
      const ext = (res.data.type || "image/jpeg").includes("png") ? "png" : "jpg";
      saveBlob(res.data, `agent100-${photo.id}.${ext}`);
    } catch (err) {
      console.error("Single download failed:", err);
      toast.error("Prenos slike ni uspel.");
    } finally {
      setDownloadingId(null);
    }
  };

  const handleDownloadAll = () => {
    if (downloadingAll || photos.length === 0) return;
    setDownloadingAll(true);
    // Stream the (potentially very large) ZIP straight to disk via a native
    // download instead of buffering it in memory as a blob.
    const a = document.createElement("a");
    a.href = `${API}/photobooth/download-all`;
    a.setAttribute("download", "");
    document.body.appendChild(a);
    a.click();
    a.remove();
    toast.message("Prenos se pripravlja...", {
      description: `${photos.length} ${photos.length === 1 ? "slika" : "slik"} v najvišji kakovosti. Pri večjem številu slik lahko traja nekaj sekund.`,
    });
    // The browser takes over the download; re-enable the button shortly after.
    setTimeout(() => setDownloadingAll(false), 4000);
  };

  return (
    <div className="film-grain min-h-screen w-full bg-[#050505] text-white">
      <div className="relative min-h-screen w-full flex flex-col px-4 sm:px-8 pt-3 pb-10">
        {/* Top bar */}
        <div className="flex items-center justify-between mb-4">
          <Link
            to="/galerija"
            className="flex items-center gap-2 text-[#A1A1AA] hover:text-white transition-colors text-sm tracking-[0.2em] uppercase"
            data-testid="downloads-back"
          >
            <ChevronLeft size={18} />
            Galerija
          </Link>
          <div className="w-16" />
        </div>

        {/* Header */}
        <div className="text-center mb-6 fade-up">
          <p className="text-[#A1A1AA] text-[10px] sm:text-xs tracking-[0.4em] uppercase">
            Agent 100
          </p>
          <h1
            className="font-display tracking-bond text-[#C9A961] text-3xl sm:text-4xl md:text-5xl uppercase mt-1"
            data-testid="downloads-title"
          >
            Prenos slik
          </h1>
          <div className="mx-auto mt-2 w-24 h-px bg-[#C9A961]/40" />
          <p className="mt-2 text-[#A1A1AA] text-sm">
            Prenesi posamezno sliko ali vse naenkrat v največji kakovosti.
          </p>

          {/* Download all */}
          <div className="mt-5 flex flex-col items-center gap-2">
            <button
              onClick={handleDownloadAll}
              disabled={downloadingAll || photos.length === 0}
              data-testid="download-all-button"
              className="inline-flex items-center justify-center gap-2 bg-[#C9A961] text-[#050505] hover:bg-[#e5c885] transition-all duration-300 px-10 sm:px-14 py-4 uppercase tracking-bond font-semibold text-sm sm:text-base active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed rounded-md"
            >
              {downloadingAll ? (
                <Loader2 size={20} className="animate-spin" />
              ) : (
                <DownloadCloud size={20} />
              )}
              {downloadingAll ? "Pripravljam..." : "Prenesi vse slike"}
            </button>
            <p className="text-[#A1A1AA] text-xs tracking-wider">
              {photos.length} {photos.length === 1 ? "slika" : "slik"} na voljo
            </p>
          </div>
        </div>

        {/* Grid */}
        {loading ? (
          <div className="flex-1 flex items-center justify-center">
            <Loader2 className="animate-spin text-[#C9A961]" size={28} />
          </div>
        ) : photos.length === 0 ? (
          <div className="flex-1 flex items-center justify-center">
            <p className="text-[#A1A1AA] tracking-[0.3em] uppercase text-sm">
              Še ni fotografij
            </p>
          </div>
        ) : (
          <div
            className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4 max-w-6xl w-full mx-auto fade-up fade-up-d1"
            data-testid="downloads-grid"
          >
            {photos.map((p) => (
              <div
                key={p.id}
                data-testid={`downloads-photo-${p.id}`}
                className="relative aspect-[3/4] rounded-lg overflow-hidden group border border-white/10 hover:border-[#C9A961]/60 transition-all duration-300"
              >
                <img
                  src={`${API}/photobooth/thumb/${p.id}`}
                  alt={STYLE_TITLES[p.style_id] || "Agent 100"}
                  loading="lazy"
                  decoding="async"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/10 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-2.5 flex flex-col gap-2">
                  <p className="text-[#C9A961] text-[10px] sm:text-xs tracking-[0.15em] uppercase truncate">
                    {STYLE_TITLES[p.style_id] || p.style_id}
                  </p>
                  <button
                    onClick={() => handleDownloadOne(p)}
                    disabled={downloadingId === p.id}
                    data-testid={`download-one-${p.id}`}
                    className="inline-flex items-center justify-center gap-2 border border-[#C9A961]/70 bg-black/40 backdrop-blur-sm text-[#C9A961] hover:bg-[#C9A961] hover:text-[#050505] transition-all duration-200 py-2 uppercase tracking-[0.15em] font-medium text-[11px] sm:text-xs active:scale-[0.98] disabled:opacity-50 rounded"
                  >
                    {downloadingId === p.id ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : (
                      <Download size={14} />
                    )}
                    Prenesi
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Toaster theme="dark" position="top-center" offset="30vh" />
    </div>
  );
}
